<script>
import { formatMinskTimestamp } from '@/scripts/tools.js';
import { INPUT_TYPES, BOOLEAN_VALUES } from '@/constants/systemSettingsConstants.js';

export default {
    props: {
        setting: {
            type: Object,
            required: true,
        },
    },
    computed: {
        displayName() {
            return this.setting.metadata?.displayName || this.setting.key;
        },
        localValue: {
            get() {
                return this.setting.value;
            },
            set(value) {
                this.updateValue(value);
            },
        },
        inputTypes() {
            return INPUT_TYPES;
        },
        booleanValues() {
            return BOOLEAN_VALUES;
        },
    },
    methods: {
        formatMinskTimestamp,

        updateValue(newValue) {
            this.$store.commit('UPDATE_SETTING_VALUE', {
                key: this.setting.key,
                value: newValue,
            });
        },

        handleCheckboxChange(event) {
            const value = event.target.checked ? BOOLEAN_VALUES.TRUE : BOOLEAN_VALUES.FALSE;
            this.updateValue(value);
        },
    },
};
</script>

<template>
    <div class="setting_row">
        <div class="setting_header">
            <label :for="`setting-${setting.id}`" class="setting_label">
                {{ displayName }}
            </label>
        </div>

        <div class="setting_input">
            <!-- Boolean: Checkbox/Toggle -->
            <label
                v-if="setting.type === 'boolean'"
                class="checkbox_container"
            >
                <input
                    :id="`setting-${setting.id}`"
                    type="checkbox"
                    :checked="setting.value === booleanValues.TRUE"
                    @change="handleCheckboxChange"
                />
                <span class="checkbox_label">
                    {{ setting.value === booleanValues.TRUE ? 'Включено' : 'Выключено' }}
                </span>
            </label>

            <!-- Integer/Float: Number Input -->
            <div v-else-if="setting.type === 'integer' || setting.type === 'float'" class="number_input_wrapper">
                <input
                    :id="`setting-${setting.id}`"
                    type="number"
                    v-model="localValue"
                    :min="setting.metadata.min"
                    :max="setting.metadata.max"
                    :step="setting.metadata.step"
                />
                <span class="input_hint">
                    Диапазон: {{ setting.metadata.min }} – {{ setting.metadata.max }}
                </span>
            </div>

            <!-- String with Options: Select -->
            <select
                v-else-if="setting.type === 'string' && setting.metadata.inputType === inputTypes.SELECT"
                :id="`setting-${setting.id}`"
                v-model="localValue"
            >
                <option
                    v-for="option in setting.metadata.options"
                    :key="option"
                    :value="option"
                >
                    {{ option }}
                </option>
            </select>

            <!-- String without Options: Text Input -->
            <input
                v-else-if="setting.type === 'string'"
                :id="`setting-${setting.id}`"
                type="text"
                v-model="localValue"
                :maxlength="setting.metadata.max"
            />
        </div>

        <p v-if="setting.description" class="setting_description">
            {{ setting.description }}
        </p>

        <p class="setting_metadata">
            Обновлено: {{ formatMinskTimestamp(setting.updated_at) }}
        </p>
    </div>
</template>

<style scoped>
.setting_row {
    margin-bottom: 20px;
    padding: 20px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background-color: var(--background);
}

.setting_header {
    margin-bottom: 12px;
}

.setting_label {
    font-weight: 600;
    font-size: 14px;
    color: var(--text-primary);
    display: block;
}

.setting_input {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
}

.checkbox_container {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.checkbox_container input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.checkbox_label {
    font-size: 14px;
    color: var(--text-primary);
}

.number_input_wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

input[type='number'],
input[type='text'],
select {
    width: 100%;
    padding: 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 14px;
    font-family: Verdana, Geneva, Tahoma, sans-serif;
    background-color: var(--input-background);
    color: var(--text-primary);
}

select {
    cursor: pointer;
}

select option {
    background: var(--background);
    color: var(--text-primary);
}

input.error,
select.error {
    border-color: #fc424a;
}

input:focus,
select:focus {
    outline: none;
    border-color: var(--main-color);
}

.input_hint {
    font-size: 11px;
    color: var(--text-secondary);
    opacity: 0.7;
}

.error_message {
    color: #fc424a;
    font-size: 12px;
    margin-top: 2px;
    display: block;
}

.setting_description {
    font-size: 12px;
    color: var(--text-secondary);
    opacity: 0.85;
    line-height: 1.5;
    margin-top: 8px;
    margin-bottom: 4px;
}

.setting_metadata {
    font-size: 12px;
    color: var(--text-tertiary);
    opacity: 0.6;
    font-style: italic;
    margin-top: 4px;
    margin-bottom: 0;
}
</style>
